import type { ComponentPropsWithoutRef } from "react";

import { cn } from "@/lib/utils";

// MUI's `Paper` under the donor theme — `@modsy/react-ui`'s `Paper` is a bare
// pass-through (`packages/react-ui/src/paper/paper.tsx:17-24`) and the theme
// carries no `MuiPaper` override, so `Paper.js` is the spec:
//
//   - `background.paper` (#fff) under `text.primary`, 4px corners
//     (`shape.borderRadius`; `square` drops them), and `transition: box-shadow
//     300ms` on `easeInOut` (`Paper.js:48-56`);
//   - **no padding and no layout** — MUI's paper is a plain block; its content
//     brings both;
//   - `variant='outlined'`: `1px solid divider` and no shadow (`:63`);
//   - `variant='elevation'` (the default) at `elevation` (default 1): the
//     theme's `shadows[elevation]`.
//
// **Elevation 1 is the donor's, not MUI's.** The theme replaces `shadows[1]`
// with a flat `0px 0px 0px 1px #e0e0e0` ring (`theme.ts:395-399`), so a
// default paper wears a grey hairline rather than MUI's three-layer drop —
// `shadow-modsy-elevation-1`. Every other step is MUI's own.
//
// Only the steps a ported screen draws are offered, each from its token; a step
// with no token is a type error rather than a silent fallback, where MUI would
// take any integer.
//
// The two community callers restyle it at the call site, as their `sx` does:
// the map-type card's own shadow and 8px radius over `elevation={4}`
// (`MapTypeToggle.tsx:41-58` — so the visible shadow is the card's), and the
// Homesites filter bar's `grey.50` ground (`HomesiteListIsland.tsx:287-294`).
// `className` is resolved last, so a caller's class wins.
//
// **The shadow rides a custom property, as MUI's does.** `Paper.js` paints
// `box-shadow: var(--Paper-shadow)` and sets the variable per elevation; here
// the step sets `--mui-paper-shadow` and the box draws `shadow-(--mui-paper-
// shadow)`. That is what lets a caller's `shadow-[…]` or `shadow-none` win:
// `tailwind-merge` sees both as one shadow-size group and keeps the later.
// Drawn as the named token (`shadow-mui-elevation-4`) it would not — the merge
// reads an unknown `shadow-*` name as a *colour*, keeps both classes, and the
// stylesheet's order decides, which put the elevation over the map card's own
// shadow.

const ELEVATION = {
  0: "[--mui-paper-shadow:0_0_#0000]",
  1: "[--mui-paper-shadow:var(--shadow-modsy-elevation-1)]",
  2: "[--mui-paper-shadow:var(--shadow-mui-elevation-2)]",
  4: "[--mui-paper-shadow:var(--shadow-mui-elevation-4)]",
  6: "[--mui-paper-shadow:var(--shadow-mui-elevation-6)]",
  16: "[--mui-paper-shadow:var(--shadow-mui-elevation-16)]",
  24: "[--mui-paper-shadow:var(--shadow-mui-elevation-24)]",
} as const;

export type MuiPaperElevation = keyof typeof ELEVATION;

export interface MuiPaperProps extends ComponentPropsWithoutRef<"div"> {
  /** `elevation` by default, as MUI's. */
  variant?: "elevation" | "outlined";
  /** The theme's `shadows[n]`; 1 by default. Ignored when outlined, as MUI does. */
  elevation?: MuiPaperElevation;
  /** No corners. */
  square?: boolean;
}

export function MuiPaper({
  variant = "elevation",
  elevation = 1,
  square = false,
  className,
  ...props
}: MuiPaperProps) {
  return (
    <div
      data-slot="mui-paper"
      {...props}
      className={cn(
        "text-lennar-ink bg-white transition-[box-shadow] duration-300 ease-in-out",
        square ? "rounded-none" : "rounded-[4px]",
        variant === "outlined"
          ? "border-lennar-divider border shadow-none"
          : cn("shadow-(--mui-paper-shadow)", ELEVATION[elevation]),
        className,
      )}
    />
  );
}
